import {
  products as mockProducts,
  productTypes as mockProductTypes,
} from '@/mocks/products';
import type { Product, ProductType, ProductTypeFilter } from '@/domain/types';
import { requireSupabase } from '@/lib/supabase';

export const products: Product[] = mockProducts.map((p) => ({
  ...p,
  typeIds: [],
  typeNames: [],
}));
export const productTypes: ProductTypeFilter[] = [...mockProductTypes];

export function getPublishedProducts() {
  return products.filter((product) => product.status === 'published');
}

export function getProductBySlug(slug: string) {
  return products.find((product) => product.slug === slug);
}

export function getProductById(id: string) {
  return products.find((product) => product.id === id);
}

export function getProductsByType(type: ProductType) {
  return products.filter((product) => product.type === type);
}

type ProductRow = {
  id: string;
  name: string;
  slug: string;
  type: ProductType;
  category_id?: string | null;
  short_description: string;
  description: string;
  features: string[];
  thumbnail_url: string | null;
  gallery: string[];
  status: Product['status'];
  price_from: number;
  is_free?: boolean;
  version: string;
  docs_html?: string | null;
  features_html?: string | null;
  changelog_html?: string | null;
  created_at: string;
  updated_at: string;
  product_type_assignments?: Array<{ product_types?: { id: string; name?: string; slug?: string } | null }> | null;
};

export interface ProductInput {
  name: string;
  slug: string;
  type: ProductType;
  categoryId?: string | null;
  typeIds: string[];
  shortDescription: string;
  description: string;
  priceFrom: number;
  isFree?: boolean;
  status: Product['status'];
  thumbnail?: string;
  version?: string;
  features?: string[];
}

const PRODUCT_SELECT = '*, product_type_assignments(product_types(id, name, slug))';

function mapProduct(row: ProductRow): Product {
  const types = row.product_type_assignments ?? [];
  return {
    id: row.id,
    name: row.name,
    slug: row.slug,
    type: row.type,
    categoryId: row.category_id ?? null,
    typeIds: types.map((a) => a.product_types?.id ?? '').filter(Boolean),
    typeNames: types.map((a) => a.product_types?.name ?? '').filter(Boolean),
    shortDescription: row.short_description,
    description: row.description,
    features: row.features ?? [],
    thumbnail: row.thumbnail_url ?? '',
    gallery: row.gallery ?? [],
    status: row.status,
    priceFrom: Number(row.price_from),
    isFree: row.is_free ?? false,
    sales: 0,
    rating: 0,
    reviews: 0,
    version: row.version,
    docsHtml: row.docs_html ?? null,
    featuresHtml: row.features_html ?? null,
    changelogHtml: row.changelog_html ?? null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function toProductRow(input: ProductInput) {
  return {
    name: input.name,
    slug: input.slug,
    type: input.type,
    category_id: input.categoryId || null,
    short_description: input.shortDescription,
    description: input.description,
    features: input.features ?? [],
    thumbnail_url: input.thumbnail || null,
    gallery: [],
    status: input.status,
    price_from: input.priceFrom,
    is_free: input.isFree ?? input.priceFrom <= 0,
    version: input.version || '1.0.0',
    updated_at: new Date().toISOString(),
  };
}

export async function listPublishedProducts(limit = 100): Promise<Product[]> {
  const supabase = requireSupabase();
  const { data, error } = await supabase
    .from('products')
    .select(PRODUCT_SELECT)
    .eq('status', 'published')
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data as ProductRow[]).map(mapProduct);
}

export async function getPublishedProductBySlug(
  slug: string,
): Promise<Product | null> {
  const supabase = requireSupabase();
  const { data, error } = await supabase
    .from('products')
    .select(PRODUCT_SELECT)
    .eq('slug', slug)
    .eq('status', 'published')
    .maybeSingle();
  if (error) throw error;
  return data ? mapProduct(data as ProductRow) : null;
}

export async function getPublishedProductById(
  id: string,
): Promise<Product | null> {
  const supabase = requireSupabase();
  const { data, error } = await supabase
    .from('products')
    .select(PRODUCT_SELECT)
    .eq('id', id)
    .eq('status', 'published')
    .maybeSingle();
  if (error) throw error;
  return data ? mapProduct(data as ProductRow) : null;
}

export interface PublicProductFile {
  id: string;
  version: string;
  changelog: string;
  requiresWp: string;
  requiresPhp: string;
  testedWp: string;
  createdAt: string;
}

export async function getPublicProductFiles(
  productId: string,
): Promise<PublicProductFile[]> {
  const supabase = requireSupabase();
  const { data, error } = await supabase
    .from('product_files')
    .select(
      'id, version, changelog, requires_wp, requires_php, tested_wp, created_at',
    )
    .eq('product_id', productId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return ((data as any[]) ?? []).map((r) => ({
    id: r.id,
    version: r.version,
    changelog: r.changelog ?? '',
    requiresWp: r.requires_wp ?? '6.2',
    requiresPhp: r.requires_php ?? '7.4',
    testedWp: r.tested_wp ?? '',
    createdAt: r.created_at,
  }));
}

export async function listAdminProducts() {
  const supabase = requireSupabase();
  const { data, error } = await supabase
    .from('products')
    .select(PRODUCT_SELECT)
    .order('created_at', { ascending: false });

  if (error) {
    throw error;
  }

  return (data as ProductRow[]).map(mapProduct);
}

export async function getAdminProduct(id: string) {
  const supabase = requireSupabase();
  const { data, error } = await supabase
    .from('products')
    .select(PRODUCT_SELECT)
    .eq('id', id)
    .maybeSingle();

  if (error) {
    throw error;
  }

  return data ? mapProduct(data as ProductRow) : null;
}

async function syncProductTypes(supabase: ReturnType<typeof requireSupabase>, productId: string, typeIds: string[]) {
  // Delete existing assignments
  await supabase.from('product_type_assignments').delete().eq('product_id', productId);
  // Insert new ones
  if (typeIds.length > 0) {
    const rows = typeIds.map((type_id) => ({ product_id: productId, type_id }));
    const { error } = await supabase.from('product_type_assignments').insert(rows);
    if (error) throw error;
  }
}

export async function createAdminProduct(input: ProductInput) {
  const supabase = requireSupabase();
  const { data, error } = await supabase
    .from('products')
    .insert(toProductRow(input))
    .select(PRODUCT_SELECT)
    .single();

  if (error) throw error;

  const product = mapProduct(data as ProductRow);
  await syncProductTypes(supabase, product.id, input.typeIds);

  // Re-fetch with types
  const { data: data2, error: error2 } = await supabase
    .from('products')
    .select(PRODUCT_SELECT)
    .eq('id', product.id)
    .single();
  if (error2) throw error2;
  return mapProduct(data2 as ProductRow);
}

export async function updateAdminProduct(id: string, input: ProductInput) {
  const supabase = requireSupabase();
  const { data, error } = await supabase
    .from('products')
    .update(toProductRow(input))
    .eq('id', id)
    .select(PRODUCT_SELECT)
    .single();

  if (error) throw error;

  await syncProductTypes(supabase, id, input.typeIds);

  // Re-fetch with types
  const { data: data2, error: error2 } = await supabase
    .from('products')
    .select(PRODUCT_SELECT)
    .eq('id', id)
    .single();
  if (error2) throw error2;
  return mapProduct(data2 as ProductRow);
}

export interface AdminProductRelease {
  id: string;
  productId: string;
  productName: string;
  version: string;
  downloadFileName: string;
  fileSize: number | null;
  accessLevel: 'free' | 'purchase';
  status: 'active' | 'archived';
  createdAt: string;
}

type ProductFileListRow = {
  id: string;
  product_id: string;
  version: string;
  download_file_name: string | null;
  file_name: string;
  file_size: number | null;
  access_level: string;
  status: string;
  created_at: string;
  products?: { name?: string | null } | null;
};

export async function listAllProductFiles(): Promise<AdminProductRelease[]> {
  const supabase = requireSupabase();
  const { data, error } = await supabase
    .from('product_files')
    .select('*, products!product_id(name)')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data as ProductFileListRow[]).map((row) => ({
    id: row.id,
    productId: row.product_id,
    productName: row.products?.name || row.product_id,
    version: row.version,
    downloadFileName: row.download_file_name ?? row.file_name,
    fileSize: row.file_size,
    accessLevel: row.access_level as 'free' | 'purchase',
    status: row.status as 'active' | 'archived',
    createdAt: row.created_at,
  }));
}

export async function deleteAdminProduct(id: string) {
  const supabase = requireSupabase();
  const { error } = await supabase.from('products').delete().eq('id', id);

  if (error) {
    throw error;
  }
}

function fileExtension(name: string) {
  const parts = name.split('.');
  return parts.length > 1 ? (parts.pop()?.toLowerCase() ?? '') : '';
}

export interface AdminProductFile {
  id: string;
  downloadFileName: string;
  originalFileName: string;
  version: string;
  fileSize: number | null;
  accessLevel: 'free' | 'purchase';
  storagePath: string;
  mediaAssetId: string | null;
  createdAt: string;
}

type ProductFileRow = {
  id: string;
  version: string;
  download_file_name: string | null;
  file_name: string;
  original_file_name: string | null;
  file_size: number | null;
  access_level: string;
  storage_path: string;
  media_asset_id: string | null;
  created_at: string;
};

const FILE_SELECT =
  'id,version,download_file_name,file_name,original_file_name,file_size,access_level,storage_path,media_asset_id,created_at';

function mapProductFile(row: ProductFileRow): AdminProductFile {
  return {
    id: row.id,
    downloadFileName: row.download_file_name ?? row.file_name,
    originalFileName: row.original_file_name ?? row.file_name,
    version: row.version,
    fileSize: row.file_size,
    accessLevel: row.access_level as 'free' | 'purchase',
    storagePath: row.storage_path,
    mediaAssetId: row.media_asset_id,
    createdAt: row.created_at,
  };
}

export async function getAdminProductFile(
  productId: string,
): Promise<AdminProductFile | null> {
  const supabase = requireSupabase();
  const { data, error } = await supabase
    .from('product_files')
    .select(FILE_SELECT)
    .eq('product_id', productId)
    .eq('status', 'active')
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return data ? mapProductFile(data as ProductFileRow) : null;
}

export async function listArchivedProductFiles(
  productId: string,
): Promise<AdminProductFile[]> {
  const supabase = requireSupabase();
  const { data, error } = await supabase
    .from('product_files')
    .select(FILE_SELECT)
    .eq('product_id', productId)
    .eq('status', 'archived')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data as ProductFileRow[]).map(mapProductFile);
}

export async function archiveAdminProductFile(fileId: string): Promise<void> {
  const supabase = requireSupabase();
  const { error } = await supabase
    .from('product_files')
    .update({ status: 'archived' })
    .eq('id', fileId);

  if (error) throw error;
}

export async function permanentlyDeleteProductFile(
  file: AdminProductFile,
): Promise<void> {
  const supabase = requireSupabase();

  const { error: storageError } = await supabase.storage
    .from('product-files')
    .remove([file.storagePath]);

  if (storageError) throw storageError;

  if (file.mediaAssetId) {
    const { error: mediaError } = await supabase
      .from('media_assets')
      .delete()
      .eq('id', file.mediaAssetId);

    if (mediaError) throw mediaError;
  }

  const { error } = await supabase
    .from('product_files')
    .delete()
    .eq('id', file.id);

  if (error) throw error;
}

export async function updateProductDocs(
  id: string,
  docsHtml: string,
): Promise<void> {
  const supabase = requireSupabase();
  const { error } = await supabase
    .from('products')
    .update({ docs_html: docsHtml, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function getProductDocs(
  slug: string,
): Promise<{ name: string; docsHtml: string | null } | null> {
  const supabase = requireSupabase();
  const { data, error } = await supabase
    .from('products')
    .select('name, docs_html')
    .eq('slug', slug)
    .eq('status', 'published')
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return {
    name: (data as any).name,
    docsHtml: (data as any).docs_html ?? null,
  };
}

export async function updateProductFeatures(
  id: string,
  html: string,
): Promise<void> {
  const supabase = requireSupabase();
  const { error } = await supabase
    .from('products')
    .update({ features_html: html, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export interface CustomerProductFile {
  id: string;
  productId: string;
  productName: string;
  slug: string;
  version: string;
  changelog: string;
  requiresWp: string;
  requiresPhp: string;
  testedUpTo: string;
  status: string;
  storagePath: string;
  createdAt: string;
}

export async function listCustomerProductFiles(): Promise<CustomerProductFile[]> {
  const supabase = requireSupabase();
  const { data, error } = await supabase
    .from('product_files')
    .select('*, products!product_id(name, slug)')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return ((data as any[]) ?? []).map((r) => ({
    id: r.id,
    productId: r.product_id,
    productName: r.products?.name ?? r.product_id,
    slug: r.products?.slug ?? r.product_id,
    version: r.version,
    changelog: r.changelog ?? '',
    requiresWp: r.requires_wp ?? '6.2',
    requiresPhp: r.requires_php ?? '7.4',
    testedUpTo: r.tested_wp ?? '',
    status: r.status ?? 'stable',
    storagePath: r.storage_path,
    createdAt: r.created_at,
  }));
}

export async function downloadProductFileUrl(fileId: string, downloadName?: string): Promise<string> {
  const supabase = requireSupabase();
  const { data, error } = await supabase
    .from('product_files')
    .select('storage_path')
    .eq('id', fileId)
    .single();

  if (error) throw error;
  if (!data) throw new Error('File not found.');

  const { data: signed, error: signedError } = await supabase.storage
    .from('product-files')
    .createSignedUrl(data.storage_path, 60, {
      download: downloadName || true,
    });

  if (signedError) throw signedError;
  return signed.signedUrl;
}

export async function updateProductChangelog(
  id: string,
  html: string,
): Promise<void> {
  const supabase = requireSupabase();
  const { error } = await supabase
    .from('products')
    .update({ changelog_html: html, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function uploadAdminProductFile(
  product: Product,
  file: File,
  accessLevel: 'free' | 'purchase',
) {
  const supabase = requireSupabase();
  const extension = fileExtension(file.name);
  const generatedKey = crypto.randomUUID();
  const storagePath = `products/${product.id}/${generatedKey}${extension ? `.${extension}` : ''}`;
  const date = new Date().toISOString().slice(0, 10);
  const downloadFileName = `${product.slug}-${product.version}-${date}${extension ? `.${extension}` : ''}`;

  const { error: uploadError } = await supabase.storage
    .from('product-files')
    .upload(storagePath, file, {
      contentType: file.type || undefined,
      upsert: false,
    });

  if (uploadError) throw uploadError;

  const { data: media, error: mediaError } = await supabase
    .from('media_assets')
    .insert({
      bucket: 'product-files',
      storage_path: storagePath,
      original_file_name: file.name,
      display_name: downloadFileName,
      file_extension: extension,
      mime_type: file.type || null,
      file_size: file.size,
      visibility: 'private',
      asset_type: 'product_file',
    })
    .select('id')
    .single();

  if (mediaError) throw mediaError;

  const { error } = await supabase.from('product_files').insert({
    product_id: product.id,
    version: product.version,
    storage_path: storagePath,
    file_name: downloadFileName,
    media_asset_id: media.id,
    original_file_name: file.name,
    download_file_name: downloadFileName,
    file_extension: extension,
    mime_type: file.type || null,
    file_size: file.size,
    generated_key: generatedKey,
    access_level: accessLevel,
    status: 'active',
  });

  if (error) throw error;
}
